import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CreditCard } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useMessage } from "../contexts/MessageContext";
import { useAuth } from "../contexts/AuthContext";
import { Loading } from "./common/Loading";
import { Table } from "./common/Table";

const apiUrl =
  window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

const PAYMENT_STATUS_BADGE = {
  completed: "bg-green-100 text-green-800",
  paid: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  failed: "bg-red-100 text-red-800",
  unpaid: "bg-gray-100 text-gray-800",
};

const ORDER_PAYMENT_STATUS_BADGE = {
  paid: "bg-green-100 text-green-800",
  partial: "bg-yellow-100 text-yellow-800",
  unpaid: "bg-red-100 text-red-800",
};

function formatMethod(value) {
  if (!value || typeof value !== "string") return "—";
  return value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const PaymentSection = ({ orderId, orderNumber, orderTotal }) => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const token = getToken ? getToken() : localStorage.getItem("token");

  const [paymentSummary, setPaymentSummary] = useState({});
  const [payments, setPayments] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { showSuccess, showError } = useMessage();

  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    method: "bank_transfer",
    date: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const paymentMethods = [
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cash", label: "Cash" },
    { value: "credit_card", label: "Credit Card" },
    { value: "debit_card", label: "Debit Card" },
    { value: "check", label: "Check" },
    { value: "other", label: "Other" },
  ];

  const paymentColumns = useMemo(
    () => [
      {
        key: "date",
        label: "Date",
        sortable: true,
        render: (v) =>
          v ? new Date(v).toLocaleDateString() : "—",
      },
      {
        key: "method",
        label: "Method",
        sortable: true,
        render: (value) => (
          <span className="text-gray-900">{formatMethod(value)}</span>
        ),
      },
      {
        key: "amount",
        label: "Amount",
        sortable: true,
        render: (v) => (
          <span className="tabular-nums font-medium">
            ${parseFloat(v || 0).toFixed(2)}
          </span>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        render: (value) => {
          const s = String(value || "").toLowerCase();
          const cls =
            PAYMENT_STATUS_BADGE[s] || "bg-gray-100 text-gray-800";
          return (
            <span
              className={`px-2 py-1 rounded text-xs font-medium capitalize ${cls}`}
            >
              {value || "—"}
            </span>
          );
        },
      },
      {
        key: "notes",
        label: "Notes",
        sortable: false,
        render: (value) =>
          value ? (
            <span className="text-gray-600 text-sm line-clamp-2 max-w-xs">
              {value}
            </span>
          ) : (
            "—"
          ),
      },
    ],
    []
  );

  const fetchPaymentData = useCallback(async () => {
    if (!orderId || !token) return;
    try {
      const headers = { "x-auth-token": token };
      const [summaryRes, paymentsRes] = await Promise.all([
        axios.get(`${apiUrl}/payment/order/${orderId}/summary`, { headers }),
        axios.get(`${apiUrl}/payment/order/${orderId}/payments`, { headers }),
      ]);
      setPaymentSummary(summaryRes.data?.data || {});
      setPayments(paymentsRes.data?.data || []);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to load payment data.";
      showError(msg);
    } finally {
      setInitialLoad(false);
    }
  }, [orderId, token, showError]);

  useEffect(() => {
    if (orderId && token) {
      setInitialLoad(true);
      fetchPaymentData();
    }
  }, [fetchPaymentData, orderId, token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (paymentForm.amount === "" || Number.isNaN(Number(paymentForm.amount))) {
      showError("Please enter a valid amount.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${apiUrl}/payment/order/${orderId}/collect`,
        {
          amount: parseFloat(paymentForm.amount),
          method: paymentForm.method,
          date: paymentForm.date,
          notes: paymentForm.notes,
        },
        { headers: { "x-auth-token": token } }
      );

      await fetchPaymentData();

      setPaymentForm({
        amount: "",
        method: "bank_transfer",
        date: new Date().toISOString().split("T")[0],
        notes: "",
      });
      setShowModal(false);
      showSuccess("Payment recorded successfully.");
    } catch (error) {
      console.error("Error collecting payment:", error);
      showError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to record payment. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const summaryStatus = String(paymentSummary?.status || "").toLowerCase();
  const statusBadgeClass =
    ORDER_PAYMENT_STATUS_BADGE[summaryStatus] ||
    "bg-gray-100 text-gray-800";

  const displayOrderTotal = Number(
    paymentSummary?.orderTotal ?? orderTotal ?? 0
  );
  const displayPaid = Number(paymentSummary?.totalPaid ?? 0);
  const displayBalance = Number(paymentSummary?.balance ?? 0);

  if (initialLoad) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      {/* Summary + collect */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Payment summary
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {orderNumber
                ? `Order ${orderNumber}`
                : "Payments for this order"}
            </p>
          </div>
          {paymentSummary && (
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium capitalize shrink-0 ${statusBadgeClass}`}
            >
              {paymentSummary.status
                ? String(paymentSummary.status).replace(/_/g, " ")
                : "—"}
            </span>
          )}
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-md border border-gray-200 bg-white px-3 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Order total
            </dt>
            <dd className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">
              ${displayOrderTotal.toFixed(2)}
            </dd>
          </div>
          <div className="rounded-md border border-gray-200 bg-white px-3 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Total paid
            </dt>
            <dd className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">
              ${displayPaid.toFixed(2)}
            </dd>
          </div>
          <div className="rounded-md border border-gray-200 bg-white px-3 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Balance due
            </dt>
            <dd className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">
              ${displayBalance.toFixed(2)}
            </dd>
          </div>
        </dl>

        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowModal(true)}
            disabled={submitting}
            className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-50"
          >
            <CreditCard className="w-4 h-4 shrink-0" aria-hidden />
            Collect payment
          </button>
        </div>
      </div>

      {/* History */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Payment history
          <span className="ml-2 px-2 py-0.5 text-xs font-semibold text-white bg-gray-500 rounded-full align-middle">
            {payments.length}
          </span>
        </h3>
        <Table
          data={payments}
          columns={paymentColumns}
          paginated
          pageSize={10}
          rowKey="uuid"
          emptyMessage="No payments recorded for this order yet."
          onRowClick={(row) => row?.uuid && navigate(`/payment/${row.uuid}`)}
        />
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="collect-payment-title"
        >
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
              <h3
                id="collect-payment-title"
                className="text-lg font-semibold text-gray-900"
              >
                Collect payment
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {orderNumber
                  ? `${orderNumber} — balance due `
                  : "Balance due "}
                <span className="font-semibold text-gray-900 tabular-nums">
                  ${displayBalance.toFixed(2)}
                </span>
              </p>
            </div>

            <div className="px-4 py-4 sm:px-6 sm:py-5 space-y-4">
              <div>
                <label
                  htmlFor="payment-amount"
                  className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1"
                >
                  Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                    $
                  </span>
                  <input
                    id="payment-amount"
                    type="number"
                    name="amount"
                    value={paymentForm.amount}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    max={displayBalance > 0 ? displayBalance : undefined}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent tabular-nums"
                    placeholder="0.00"
                  />
                </div>
                {displayBalance > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setPaymentForm((p) => ({
                        ...p,
                        amount: String(displayBalance),
                      }))
                    }
                    className="text-sm text-blue-700 hover:underline mt-1"
                  >
                    Pay full balance (${displayBalance.toFixed(2)})
                  </button>
                )}
              </div>

              <div>
                <label
                  htmlFor="payment-method"
                  className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1"
                >
                  Method
                </label>
                <select
                  id="payment-method"
                  name="method"
                  value={paymentForm.method}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  {paymentMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="payment-date"
                  className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1"
                >
                  Date
                </label>
                <input
                  id="payment-date"
                  type="date"
                  name="date"
                  value={paymentForm.date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label
                  htmlFor="payment-notes"
                  className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1"
                >
                  Notes (optional)
                </label>
                <textarea
                  id="payment-notes"
                  name="notes"
                  value={paymentForm.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Reference, check #, etc."
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 border-t border-gray-200 px-4 py-3 sm:px-6 sm:justify-end">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                disabled={submitting}
                className="btn-secondary w-full sm:w-auto disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-primary w-full sm:w-auto disabled:opacity-50"
              >
                {submitting ? "Processing…" : "Record payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSection;
